import moment from 'moment';

/**
 * One call to the domain-linking picker endpoint. `accountId` selects which linked Google
 * account's Search Console sites come back in `domains`.
 */
const requestLinkPage = async (accountId) => {
    const query = accountId ? `?account=${encodeURIComponent(accountId)}` : '';

    const response = await fetch(`/api/v1/domains/link/${query}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': window.csrf_token,
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data?.detail ?? 'Failed to load domains');
    }

    return data;
};

/**
 * Search Console properties available to the current user.
 *
 * /api/v1/domains/link/ only returns `domains` once it is told which linked Google account to
 * read them from — called with no `account` parameter it short-circuits to an empty list
 * (`LinkDomainPageService._get_account_and_sites` returns `(None, [])` for a falsy account_id).
 * So the list is loaded in two steps: one call to discover the user's linked accounts, then one
 * call per account to pull its sites.
 *
 * Those per-account calls are independent on purpose — an account whose Search Console lookup
 * fails contributes nothing rather than emptying the whole report. Sites are deduplicated by
 * `siteUrl` because two linked accounts can both have access to the same property, and the table
 * keys its rows on that value.
 */
export const getUserDomains = async () => {
    const { account, accounts } = await requestLinkPage();

    // Whichever account the service considers "selected" comes back as `account` and is excluded
    // from `accounts`, so both have to be read to see every linked account.
    const accountIds = [
        ...new Set([account?.a_id, ...(accounts ?? []).map((item) => item?.a_id)].filter(Boolean)),
    ];

    if (!accountIds.length) return [];

    const results = await Promise.allSettled(accountIds.map((id) => requestLinkPage(id)));

    const sitesByUrl = new Map();
    for (const result of results) {
        if (result.status !== 'fulfilled') {
            console.log(result.reason);
            continue;
        }
        for (const site of result.value?.domains ?? []) {
            if (site?.siteUrl && !sitesByUrl.has(site.siteUrl)) {
                sitesByUrl.set(site.siteUrl, site);
            }
        }
    }

    return [...sitesByUrl.values()];
};

// Kept as a fallback fixture for working on the report without hitting Search Console: swap
// `getUserDomains` above for `Promise.resolve(HARDCODED_DOMAINS)` to render the table offline.
// const HARDCODED_DOMAINS = [
//     { siteUrl: 'https://ambrosiaforheads.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://www.newzdash.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://amplify.upworthy.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'sc-domain:gddash.com', permissionLevel: 'siteOwner' },
//     { siteUrl: 'sc-domain:essentiallysports.com', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'sc-domain:good.is', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://www.outkick.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'sc-domain:lapost.com', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'http://ambrosiaforheads.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'sc-domain:iheartdogs.com', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'sc-domain:inc.com', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://scoop.upworthy.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://www.newsweek.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://www.upworthy.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://goodinc.com/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'sc-domain:fastcompany.com', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'sc-domain:dermsquared.com', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://fandomwire.com/', permissionLevel: 'siteRestrictedUser' },
//     { siteUrl: 'https://www.sportingnews.com/us/', permissionLevel: 'siteFullUser' },
//     { siteUrl: 'https://www3.nhk.or.jp/nhkworld/', permissionLevel: 'siteRestrictedUser' },
// ].map((item) => ({ ...item, name: item.siteUrl }));

/**
 * Thin wrapper around the live GSC proxy endpoint. The endpoint forwards the raw Search
 * Analytics request body to the Cloud Function using the OAuth credentials linked to
 * `domain_id`, which is why every query needs a credentials domain even when the property being
 * queried (`queries[].domain`) is a different Search Console site.
 */
export const gscTestQuery = (data) =>
    fetch('/api/v1/domains/gsc-test-query/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': window.csrf_token,
        },
        body: JSON.stringify(data),
    });

/**
 * Builds the Search Analytics request body for one property: daily totals, no dimension
 * filters, so the response carries one row per day in the window.
 */
export function buildDomainResource({ startDate, endDate }) {
    return {
        aggregationType: 'AUTO',
        dataState: 'ALL',
        startDate,
        endDate,
        type: 'WEB',
        dimensions: ['date'],
    };
}

/**
 * Fetches daily clicks/impressions for one Search Console property and returns them as
 * `[{ date, clicks, impressions }]` sorted ascending by date. Days with no data are simply
 * absent from the GSC response; the caller fills the gaps against its own date axis.
 *
 * Rejects on transport errors and on a non-200 payload so the caller can mark that one row as
 * failed without affecting the other properties.
 */
export async function fetchDomainDailyMetrics({
    credentialsDomainId,
    siteUrl,
    startDate,
    endDate,
}) {
    const body = {
        domain_id: credentialsDomainId,
        queries: [
            {
                resource: buildDomainResource({ startDate, endDate }),
                domain: siteUrl,
                key: 'data',
            },
        ],
        max_count: 10000,
    };

    const response = await gscTestQuery(body);
    const data = await response.json();

    if (data?.status !== 200) {
        throw new Error(data?.message ?? data?.detail ?? 'GSC query failed');
    }

    const rows = data?.results?.data ?? [];

    return rows
        .map((row) => ({
            // keys are ordered as the requested dimensions: ['date']
            date: row.keys?.[0],
            clicks: row.clicks ?? 0,
            impressions: row.impressions ?? 0,
        }))
        .filter((row) => !!row.date)
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
}
